/**
 * Wake lock (SD-3, STAGE-DESIGN §2) — keep the iPad screen awake in performance mode.
 *
 * The top non-bridge gig failure is the iPad sleeping (or Safari evicting the
 * tab) mid-set. Two strategies, tried in order on performance-mode entry:
 *
 *  1. **Screen Wake Lock API** — `navigator.wakeLock.request('screen')`. The
 *     real thing, but only exists in a SECURE CONTEXT (https / localhost). On
 *     the iPad gig reality (`http://<lan-ip>:8765`, DEC-074) it's `undefined`,
 *     so we fall through. Verifiable now on desktop localhost + future LAN TLS.
 *
 *  2. **Silent-video fallback** (the NoSleep.js pattern) — iOS keeps the
 *     screen awake while a video plays. A 2×2, muted, `playsinline`, looping
 *     `<video>` fed by a `canvas.captureStream()` (no embedded blob — the
 *     stream is generated in-page, so there's zero risk of shipping a corrupt
 *     base64 clip). Muted inline autoplay is allowed on iOS without a gesture.
 *
 * Both strategies drop when the tab backgrounds, so we re-acquire on
 * `visibilitychange` while performance mode still wants the lock. The RUNBOOK
 * belt-and-braces (iPad Settings → Auto-Lock: Never on gig days) remains the
 * real guarantee; this is best-effort and the pre-flight (SD-13 row 9) reports
 * `wakeLockMode()` so the operator knows whether to trust it.
 *
 * NOTE: the video-fallback path can only be verified ON AN IPAD — desktop
 * always takes the API path. iPad eyes-on is owed.
 */

import { createSignal } from 'solid-js';

export type WakeLockMode = 'none' | 'api' | 'video' | 'unavailable';

const [wakeLockMode, setWakeLockMode] = createSignal<WakeLockMode>('none');
export { wakeLockMode };

// True between acquire() and release() — the "we WANT the screen awake" intent.
// The visibilitychange handler re-acquires only while this holds.
let wanted = false;
let listenerBound = false;

// Strategy 1 state.
let sentinel: WakeLockSentinel | null = null;
// Strategy 2 state.
let video: HTMLVideoElement | null = null;
let stream: MediaStream | null = null;
let rafId = 0;

interface WakeLockNavigator {
  wakeLock?: { request(type: 'screen'): Promise<WakeLockSentinel> };
}

async function tryApi(): Promise<boolean> {
  const wl = (navigator as unknown as WakeLockNavigator).wakeLock;
  if (!wl || typeof wl.request !== 'function') return false;
  try {
    sentinel = await wl.request('screen');
    // The API auto-releases when the tab backgrounds; null our ref so a
    // later visibilitychange re-acquires cleanly.
    sentinel.addEventListener?.('release', () => { sentinel = null; });
    return true;
  } catch {
    return false;
  }
}

function startVideoFallback(): boolean {
  try {
    const canvas = document.createElement('canvas');
    canvas.width = 2;
    canvas.height = 2;
    const ctx = canvas.getContext('2d');
    const cap = (canvas as unknown as { captureStream?: (fps?: number) => MediaStream }).captureStream;
    if (!ctx || typeof cap !== 'function') return false;
    stream = cap.call(canvas, 2);
    // Animate a pixel so the stream keeps producing frames — a fully static
    // captureStream can get dropped by the compositor, which defeats the
    // keep-awake.
    const paint = () => {
      ctx.fillStyle = (performance.now() & 256) ? '#000000' : '#010101';
      ctx.fillRect(0, 0, 2, 2);
      rafId = requestAnimationFrame(paint);
    };
    paint();

    video = document.createElement('video');
    video.muted = true;
    video.loop = true;
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('aria-hidden', 'true');
    Object.assign(video.style, {
      position: 'fixed', bottom: '0', left: '0',
      width: '2px', height: '2px', opacity: '0', pointerEvents: 'none',
    });
    video.srcObject = stream;
    document.body.appendChild(video);
    void video.play().catch(() => { /* gesture-gated on some engines; pre-flight warns */ });
    return true;
  } catch {
    return false;
  }
}

function stopVideoFallback(): void {
  if (rafId) { cancelAnimationFrame(rafId); rafId = 0; }
  if (video) {
    try { video.pause(); } catch { /* */ }
    video.srcObject = null;
    video.remove();
    video = null;
  }
  if (stream) {
    for (const track of stream.getTracks()) track.stop();
    stream = null;
  }
}

async function reacquire(): Promise<void> {
  // Already holding via the API? nothing to do. Video keeps playing across
  // most visibility flips, but re-play() is cheap + covers iOS pausing it.
  if (sentinel) { setWakeLockMode('api'); return; }
  if (await tryApi()) { setWakeLockMode('api'); stopVideoFallback(); return; }
  if (video) { void video.play().catch(() => {}); setWakeLockMode('video'); return; }
  setWakeLockMode(startVideoFallback() ? 'video' : 'unavailable');
}

function onVisibility(): void {
  if (!wanted) return;
  if (document.visibilityState === 'visible') void reacquire();
}

/** Acquire the wake lock (call on performance-mode entry). Idempotent. */
export async function acquireWakeLock(): Promise<void> {
  wanted = true;
  if (!listenerBound) {
    document.addEventListener('visibilitychange', onVisibility);
    listenerBound = true;
  }
  if (await tryApi()) {
    setWakeLockMode('api');
    return;
  }
  setWakeLockMode(startVideoFallback() ? 'video' : 'unavailable');
}

/** Release the wake lock (call on performance-mode exit). Idempotent. */
export function releaseWakeLock(): void {
  wanted = false;
  if (sentinel) {
    void sentinel.release().catch(() => {});
    sentinel = null;
  }
  stopVideoFallback();
  setWakeLockMode('none');
}
